import React from 'react'
import { ScrollView } from 'react-native'

import { ScreenContainerWithMenuHeader } from '../../components/layout/ScreenContainer'
import { Flex } from '../../components/FlexBox'
import { H4, H5, Text } from '../../components/common/Text'
import DraggableList from '../../components/common/DraggableList'
import SmallButton from './components/SmallButton'
import useTranslation from '../../hooks/useTranslation'
import useLinkToScreen from '../../hooks/useLinkToScreen'
import usePreventNavigationOnDirtyState from '../../hooks/usePreventNavigationOnDirtyState'
import useContentFeeds from './hooks/useContentFeeds'
import ContentFeedRow from './components/ContentFeedRow'
import HeroFeedPanel from './components/HeroFeedPanel'
import PublishBar from './components/PublishBar'
import NewContentFeedModal from './components/NewContentFeedModal'
import SelectContentModal from './components/SelectContentModal'
import FeedSettingsModal from './components/FeedSettingsModal'
import { TableCol, TableContainer, TableRow } from './components/Table'
import { IContentFeed } from './queries/getContentFeeds'

export const ContentFeedListScreen = () => {
  const { t } = useTranslation()
  const linkToScreen = useLinkToScreen()

  const {
    loading,
    error,
    heroFeed,
    heroBoosts,
    boostsLoading,
    boostsError,
    heroFeedSettings,
    updateHeroSettings,
    isHeroSettingsOpen,
    openHeroSettings,
    closeHeroSettings,
    applyHeroSettings,
    toggleHeroEnabled,
    heroChangeCount,
    heroPublishBlockedReason,
    heroSettingsChanges,
    heroPublishing,
    publishHero,
    discardHero,
    reorderHeroBoosts,
    removeHeroBoost,
    heroSlotIndex,
    chooseHeroSlot,
    closeHeroSelect,
    confirmHeroSelection,
    orderedFeeds,
    isCreateOpen,
    openCreate,
    closeCreate,
    createFeed,
    reorder,
    feedOrderChanges,
    orderPublishing,
    publishFeedOrder,
    discardFeedOrder
  } = useContentFeeds()

  // Two independent drafts live on this page -- the featured band and the feed
  // order -- and neither survives a reload, so warn before leaving with either
  // outstanding. Same guard the detail page uses.
  usePreventNavigationOnDirtyState({
    dirty: heroChangeCount > 0 || feedOrderChanges > 0
  })

  const configure = (feed: IContentFeed) =>
    linkToScreen('ContentFeedDetail', { id: feed.id })

  return (
    <ScreenContainerWithMenuHeader screenCategory="contentCuration">
      <ScrollView>
        <Flex flexDirection="row" mx={4} my={3} alignItems="center">
          <Flex flex={1}>
            <H4>{t('contentCuration:list:title')}</H4>
          </Flex>
        </Flex>
        <Flex mx={4} mb={3}>
          <Text>{t('contentCuration:list:description')}</Text>
        </Flex>

        {!!heroFeed && (
          <>
            <HeroFeedPanel
              boosts={heroBoosts}
              loading={boostsLoading}
              error={boostsError}
              enabled={heroFeedSettings.enabled}
              changeCount={heroChangeCount}
              publishing={heroPublishing}
              publishBlockedReason={heroPublishBlockedReason}
              onPublish={publishHero}
              onDiscard={discardHero}
              onToggleEnabled={toggleHeroEnabled}
              onConfigure={openHeroSettings}
              onReorder={reorderHeroBoosts}
              onRemove={removeHeroBoost}
              onChoose={chooseHeroSlot}
            />
            <FeedSettingsModal
              open={isHeroSettingsOpen}
              close={closeHeroSettings}
              settings={heroFeedSettings}
              slug={heroFeed.slug}
              itemCount={heroBoosts.length}
              onChange={updateHeroSettings}
              onApply={applyHeroSettings}
              dirty={heroSettingsChanges > 0}
              saving={heroPublishing}
            />
            <SelectContentModal
              open={heroSlotIndex !== null}
              close={closeHeroSelect}
              contentFeedId={heroFeed.id}
              feedTopicIds={heroFeed.topics.map(topic => topic.id)}
              currentBoosts={heroBoosts}
              mode="replace"
              onConfirm={confirmHeroSelection}
              saving={heroPublishing}
            />
          </>
        )}

        {/* Row direction is load-bearing -- see the note in HeroFeedPanel. */}
        <Flex mx={4} mb={3} flexDirection="row">
          <TableContainer>
            <Flex flexDirection="row" alignItems="center" p={2}>
              <Flex flex={1}>
                <H5 styles={{ fontWeight: '700' }}>
                  {t('contentCuration:rows:title')}
                </H5>
                <Text styles={{ fontSize: 12 }}>
                  {t('contentCuration:rows:description')}
                </Text>
              </Flex>
              <SmallButton
                label={t('contentCuration:create:new')}
                onPress={openCreate}
                variant="primary"
              />
            </Flex>

            {!loading && !error && (
              <DraggableList
                items={orderedFeeds}
                keyExtractor={feed => feed.id}
                onReorder={reorder}
                renderItem={(feed, index) => (
                  <ContentFeedRow
                    feed={feed}
                    position={index + 1}
                    onConfigure={() => configure(feed)}
                  />
                )}
              />
            )}
            {loading && (
              <TableRow>
                <TableCol>
                  <Text>{t('common:loading')}</Text>
                </TableCol>
              </TableRow>
            )}
            {!loading && !error && orderedFeeds.length === 0 && (
              <TableRow>
                <TableCol>
                  <Text>{t('contentCuration:list:empty')}</Text>
                </TableCol>
              </TableRow>
            )}
            {!!error && (
              <TableRow>
                <TableCol>
                  <Text>{t('contentCuration:list:error')}</Text>
                </TableCol>
              </TableRow>
            )}

            <PublishBar
              changeCount={feedOrderChanges}
              publishing={orderPublishing}
              onPublish={publishFeedOrder}
              onDiscard={discardFeedOrder}
            />
          </TableContainer>
        </Flex>

        <NewContentFeedModal
          open={isCreateOpen}
          close={closeCreate}
          onCreate={createFeed}
        />
      </ScrollView>
    </ScreenContainerWithMenuHeader>
  )
}

export default ContentFeedListScreen
